import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { CreceLogo } from "@/components/CreceLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { setToken(params.get("token") || ""); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(data.message);
      setTimeout(() => navigate("/auth"), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-stone-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <CreceLogo size="md" theme="dark" />
          <span className="font-display font-black text-2xl">Crece</span>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Set a new password</span>
          <h1 className="font-display text-3xl font-black tracking-tight mt-2">Reset password</h1>

          {!token && <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">Missing reset token. Please request a new reset link.</div>}

          {done ? (
            <div className="mt-6 text-center" data-testid="reset-done-state">
              <CheckCircle2 className="w-12 h-12 text-emerald-700 mx-auto" />
              <div className="font-display font-black text-xl mt-3">Password updated!</div>
              <div className="text-sm text-stone-500 mt-1">Redirecting to sign in…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="font-semibold text-sm">New password</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                  <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required
                    placeholder="Min 8 chars, 1 letter + 1 digit" className="pl-10 h-12 rounded-lg border-stone-300" />
                </div>
              </div>
              <div>
                <Label className="font-semibold text-sm">Confirm new password</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                  <Input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required
                    className="pl-10 h-12 rounded-lg border-stone-300" />
                </div>
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading || !token}
                className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                {loading ? "Saving..." : "Reset password"} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          )}

          <div className="mt-6 pt-5 border-t border-stone-200 text-center">
            <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-700 hover:text-emerald-900">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
